import type { NotificationEscalation } from '../api/types'

/** The Escalation form's rules, the server's own (alerting/escalation.py):
 *  whole hours from 1 to 720, the third notice after the second. The server
 *  decides regardless; this only keeps a bad number from being sent. */
export const MAX_ESCALATION_HOURS = 720

/** The form holds hours as typed, so a half-typed number is not lost. */
export interface EscalationDraft {
  enabled: boolean
  second: { after_h: string; people: number[] }
  third: { after_h: string; people: number[] }
}

export const draftOf = (value: NotificationEscalation): EscalationDraft => ({
  enabled: value.enabled,
  second: { after_h: String(value.second.after_h), people: [...value.second.people] },
  third: { after_h: String(value.third.after_h), people: [...value.third.people] },
})

const wholeHours = (text: string): number | null => {
  if (!/^\d+$/.test(text.trim())) return null
  const hours = Number(text)
  return hours >= 1 && hours <= MAX_ESCALATION_HOURS ? hours : null
}

/** What is wrong with the draft, in a sentence, or null. */
export function escalationProblem(draft: EscalationDraft): string | null {
  const second = wholeHours(draft.second.after_h)
  const third = wholeHours(draft.third.after_h)
  if (second === null || third === null) return `Hours are whole hours from 1 to ${MAX_ESCALATION_HOURS}.`
  if (third <= second) return 'The third notice must come after the second.'
  return null
}

/** The body to send, or null while `escalationProblem` has something to say. */
export function escalationBody(draft: EscalationDraft): NotificationEscalation | null {
  if (escalationProblem(draft)) return null
  return {
    enabled: draft.enabled,
    second: { after_h: Number(draft.second.after_h), people: draft.second.people },
    third: { after_h: Number(draft.third.after_h), people: draft.third.people },
  }
}

export const toggled = (people: number[], id: number): number[] =>
  people.includes(id) ? people.filter(p => p !== id) : [...people, id]
